export type MermaidGraphDirection = 'TB' | 'TD' | 'BT' | 'LR' | 'RL';
export type MermaidGraphNodeShape =
  | 'rect'
  | 'roundedRect'
  | 'ellipse'
  | 'diamond';

export type MermaidGraphNodeIR = {
  id: string;
  label: string;
  shape: MermaidGraphNodeShape;
};

export type MermaidGraphEdgeIR = {
  id: string;
  source: string;
  target: string;
  label?: string;
  line: 'solid' | 'dashed';
  arrow: 'forward' | 'none';
};

export type MermaidGraphSubgraphIR = {
  id: string;
  label: string;
  nodeIds: string[];
};

export type MermaidUnsupportedConstruct = {
  kind: string;
  source: string;
  line: number;
  reason: string;
};

export type MermaidGraphIR = {
  version: 1;
  diagram: 'flowchart';
  direction: MermaidGraphDirection;
  nodes: MermaidGraphNodeIR[];
  edges: MermaidGraphEdgeIR[];
  subgraphs: MermaidGraphSubgraphIR[];
  unsupported: MermaidUnsupportedConstruct[];
};

const HEADER_RE = /^\s*(?:flowchart|graph)\s+(TB|TD|BT|LR|RL)\s*;?\s*$/i;
const EDGE_OPERATOR_RE = /^(?:-\.->|-\.-|-->|---)/;
const ID_RE = /^[A-Za-z_][\w.-]*$/;
const UNSUPPORTED_DIRECTIVE_RE =
  /^(?:classDef|class|style|click|linkStyle|accTitle|accDescr)\b/i;

type ParsedNode = MermaidGraphNodeIR & {
  explicit: boolean;
};

function stripQuotes(value: string): string {
  const trimmed = value.trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}

function parseNodeToken(token: string): ParsedNode | null {
  const value = token.trim().replace(/;$/, '').trim();

  const ellipse = /^([A-Za-z_][\w.-]*)\(\((.*)\)\)$/.exec(value);
  if (ellipse) {
    return {
      id: ellipse[1],
      label: stripQuotes(ellipse[2]),
      shape: 'ellipse',
      explicit: true,
    };
  }

  const diamond = /^([A-Za-z_][\w.-]*)\{(.*)\}$/.exec(value);
  if (diamond) {
    return {
      id: diamond[1],
      label: stripQuotes(diamond[2]),
      shape: 'diamond',
      explicit: true,
    };
  }

  const rect = /^([A-Za-z_][\w.-]*)\[(.*)\]$/.exec(value);
  if (rect) {
    return {
      id: rect[1],
      label: stripQuotes(rect[2]),
      shape: 'rect',
      explicit: true,
    };
  }

  const rounded = /^([A-Za-z_][\w.-]*)\((.*)\)$/.exec(value);
  if (rounded) {
    return {
      id: rounded[1],
      label: stripQuotes(rounded[2]),
      shape: 'roundedRect',
      explicit: true,
    };
  }

  if (ID_RE.test(value)) {
    return {
      id: value,
      label: value,
      shape: 'rect',
      explicit: false,
    };
  }

  return null;
}


function parseNodeTokenPrefix(
  source: string
): { node: ParsedNode; consumed: number } | null {
  const leading = /^\s*/.exec(source)?.[0].length ?? 0;
  const body = source.slice(leading);
  const id = /^[A-Za-z_][\w.-]*/.exec(body);
  if (!id) return null;

  let end = id[0].length;
  const opener = body[end];

  if (opener === '[' || opener === '{' || opener === '(') {
    let close = opener === '[' ? ']' : opener === '{' ? '}' : ')';
    let depth = 1;
    let cursor = end + 1;

    if (opener === '(' && body[cursor] === '(') {
      const closeIndex = body.indexOf('))', cursor + 1);
      if (closeIndex === -1) return null;
      end = closeIndex + 2;
    } else {
      while (cursor < body.length && depth > 0) {
        if (body[cursor] === opener) depth += 1;
        if (body[cursor] === close) depth -= 1;
        cursor += 1;
      }
      if (depth !== 0) return null;
      end = cursor;
    }
  }

  const token = body.slice(0, end);
  const node = parseNodeToken(token);
  if (!node) return null;
  return { node, consumed: leading + end };
}

type ParsedEdgeSegment = {
  source: ParsedNode;
  target: ParsedNode;
  operator: string;
  label?: string;
};

function parseEdgeChain(source: string): ParsedEdgeSegment[] | null {
  const statement = source.trim().replace(/;$/, '').trim();
  const first = parseNodeTokenPrefix(statement);
  if (!first) return null;

  const segments: ParsedEdgeSegment[] = [];
  let currentNode = first.node;
  let offset = first.consumed;

  while (offset < statement.length) {
    offset += /^\s*/.exec(statement.slice(offset))?.[0].length ?? 0;
    if (offset >= statement.length) break;

    const operatorMatch = EDGE_OPERATOR_RE.exec(statement.slice(offset));
    if (!operatorMatch) return null;
    const operator = operatorMatch[0];
    offset += operator.length;
    offset += /^\s*/.exec(statement.slice(offset))?.[0].length ?? 0;

    let label: string | undefined;
    if (statement[offset] === '|') {
      const labelEnd = statement.indexOf('|', offset + 1);
      if (labelEnd === -1) return null;
      label = stripQuotes(statement.slice(offset + 1, labelEnd));
      offset = labelEnd + 1;
      offset += /^\s*/.exec(statement.slice(offset))?.[0].length ?? 0;
    }

    const next = parseNodeTokenPrefix(statement.slice(offset));
    if (!next) return null;

    segments.push({
      source: currentNode,
      target: next.node,
      operator,
      ...(label?.trim() ? { label: label.trim() } : {}),
    });

    currentNode = next.node;
    offset += next.consumed;
  }

  return segments.length ? segments : null;
}

function parseSubgraphHeader(
  source: string,
  fallbackId: string
): { id: string; label: string } | null {
  const body = source.replace(/^subgraph\s+/i, '').trim();
  if (!body) return null;

  const declared = parseNodeToken(body);
  if (declared) {
    return { id: declared.id, label: declared.label };
  }

  const idAndLabel = /^([A-Za-z_][\w.-]*)\s+(.+)$/.exec(body);
  if (idAndLabel) {
    return { id: idAndLabel[1], label: stripQuotes(idAndLabel[2]) };
  }

  return { id: fallbackId, label: stripQuotes(body) };
}

export function parseMermaidGraphIR(source: string): MermaidGraphIR {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const unsupported: MermaidUnsupportedConstruct[] = [];
  const nodes: MermaidGraphNodeIR[] = [];
  const nodeIndex = new Map<string, number>();
  const edges: MermaidGraphEdgeIR[] = [];
  const subgraphs: MermaidGraphSubgraphIR[] = [];

  let direction: MermaidGraphDirection = 'TD';
  let headerSeen = false;
  let currentSubgraph: MermaidGraphSubgraphIR | null = null;

  const addUnsupported = (
    kind: string,
    raw: string,
    line: number,
    reason: string
  ) => {
    unsupported.push({ kind, source: raw, line, reason });
  };

  const addNode = (
    node: ParsedNode,
    subgraph: MermaidGraphSubgraphIR | null
  ) => {
    const existingIndex = nodeIndex.get(node.id);
    if (existingIndex === undefined) {
      nodeIndex.set(node.id, nodes.length);
      nodes.push({
        id: node.id,
        label: node.label,
        shape: node.shape,
      });
    } else if (node.explicit) {
      nodes[existingIndex] = {
        id: node.id,
        label: node.label,
        shape: node.shape,
      };
    }

    if (subgraph && !subgraph.nodeIds.includes(node.id)) {
      subgraph.nodeIds.push(node.id);
    }
  };

  for (let index = 0; index < lines.length; index++) {
    const raw = lines[index];
    const lineNumber = index + 1;
    const trimmed = raw.trim();

    if (!trimmed || trimmed.startsWith('%%')) continue;

    if (!headerSeen) {
      const header = HEADER_RE.exec(trimmed);
      if (!header) {
        addUnsupported(
          'diagram',
          raw,
          lineNumber,
          'Only flowchart/graph with TB, TD, BT, LR, or RL direction is supported.'
        );
        headerSeen = true;
      } else {
        direction = header[1].toUpperCase() as MermaidGraphDirection;
        headerSeen = true;
        continue;
      }
    }

    if (/^subgraph\b/i.test(trimmed)) {
      if (currentSubgraph) {
        addUnsupported(
          'nested-subgraph',
          raw,
          lineNumber,
          'Nested subgraphs are outside the V2 supported subset.'
        );
        continue;
      }

      const parsed = parseSubgraphHeader(
        trimmed,
        `subgraph-${subgraphs.length + 1}`
      );
      if (!parsed) {
        addUnsupported(
          'subgraph',
          raw,
          lineNumber,
          'Subgraph header could not be represented.'
        );
        continue;
      }

      currentSubgraph = { ...parsed, nodeIds: [] };
      subgraphs.push(currentSubgraph);
      continue;
    }

    if (/^end\s*;?$/i.test(trimmed)) {
      if (!currentSubgraph) {
        addUnsupported(
          'subgraph-end',
          raw,
          lineNumber,
          'Unexpected subgraph terminator.'
        );
      }
      currentSubgraph = null;
      continue;
    }

    if (UNSUPPORTED_DIRECTIVE_RE.test(trimmed)) {
      addUnsupported(
        'directive',
        raw,
        lineNumber,
        'Directive is not losslessly representable as native canvas objects.'
      );
      continue;
    }

    const edgeChain = parseEdgeChain(trimmed);
    if (edgeChain) {
      for (const edge of edgeChain) {
        addNode(edge.source, currentSubgraph);
        addNode(edge.target, currentSubgraph);
        edges.push({
          id: `edge-${edges.length + 1}`,
          source: edge.source.id,
          target: edge.target.id,
          ...(edge.label ? { label: edge.label } : {}),
          line: edge.operator.startsWith('-.') ? 'dashed' : 'solid',
          arrow: edge.operator.endsWith('>') ? 'forward' : 'none',
        });
      }
      continue;
    }

    const node = parseNodeToken(trimmed);
    if (node) {
      addNode(node, currentSubgraph);
      continue;
    }

    addUnsupported(
      'statement',
      raw,
      lineNumber,
      'Statement is outside the V2 supported flowchart subset.'
    );
  }

  if (currentSubgraph) {
    addUnsupported(
      'subgraph',
      `subgraph ${currentSubgraph.id}`,
      lines.length,
      'Subgraph is missing a closing end statement.'
    );
  }

  return {
    version: 1,
    diagram: 'flowchart',
    direction,
    nodes,
    edges,
    subgraphs,
    unsupported,
  };
}
